import React, { useState, useEffect, useCallback } from 'react';
import { getIconForCategory, getImageUrl } from './AppUtils.js';

// ============================================================================
// PRODUCT VIEWER COMPONENT WITH ARROW CONTROLS - LCP OPTIMIZED (SIMPLE FIX)
// ============================================================================

// Product Viewer Component with Manual Navigation Controls
const ProductViewer = ({
  currentProducts,
  selectedProduct,
  viewState,
  mainCategories,
  selectedSubCategory,
  selectedMainCategory,
  baseConfig,
  categoryConfig = {},
  productInBuild
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [baseImages, setBaseImages] = useState([]);

  // Parse Media Gallery from baseConfig
  useEffect(() => {
    if (baseConfig) {
      // Try both possible field names
      const mediaGalleryField = baseConfig.mediaGallery || baseConfig["Media Gallery"];
      
      if (mediaGalleryField) {
        try {
          let mediaGallery;
          
          // Handle if mediaGallery is already parsed or needs parsing
          if (typeof mediaGalleryField === 'string') {
            mediaGallery = JSON.parse(mediaGalleryField);
          } else {
            mediaGallery = mediaGalleryField;
          }
          
          // Extract image URLs
          const images = mediaGallery
            .filter(item => item.type === 'image' && item.src)
            .map(item => getImageUrl(item.src))
            .filter(url => url !== null);
          
          setBaseImages(images);
          setCurrentImageIndex(0); // Reset to first image when images change
          
        } catch (error) {
          setBaseImages([]);
        }
      } else {
        setBaseImages([]);
      }
    } else {
      setBaseImages([]);
    }
  }, [baseConfig]);

  // Reset image index when switching view states or when images change
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [viewState, baseImages.length]);

  // Navigation functions
  const goToPrevious = useCallback(() => {
    setCurrentImageIndex(prev =>
      prev === 0 ? baseImages.length - 1 : prev - 1
    );
  }, [baseImages.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex(prev =>
      prev === baseImages.length - 1 ? 0 : prev + 1
    );
  }, [baseImages.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (viewState === 'main' && baseImages.length > 1) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goToPrevious();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goToNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [viewState, baseImages.length, goToNext, goToPrevious]);

  // Get the actual product to display - fallback to first product if selectedProduct is invalid
  const productToShow = currentProducts.length > 0 
    ? currentProducts[Math.min(selectedProduct, currentProducts.length - 1)] 
    : null;

  return (
    <div className="product-viewer-responsive w-full aspect-[4/3] relative overflow-hidden mx-auto max-w-[320px] sm:max-w-sm md:max-w-md lg:max-w-2xl">
        {productToShow && viewState === 'sub' && currentProducts.length > 0 ? (
        <div className="w-full h-full p-2 lg:p-6">
          {(() => {
            const imageUrl = getImageUrl(productToShow.image);
            return imageUrl ? (
              <img 
                src={imageUrl}
                alt={productToShow.name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.querySelector('.viewer-fallback').style.display = 'flex';
                }}
              />
            ) : null;
          })()}
          <div className="viewer-fallback w-full h-full bg-zinc-400 rounded-lg items-center justify-center text-zinc-600 text-5xl lg:text-[20rem]" style={{ display: getImageUrl(productToShow.image) ? 'none' : 'flex' }}>
            {getIconForCategory(selectedSubCategory, categoryConfig)}
          </div>
        </div>
      ) : viewState === 'main' ? (
        <div className="w-full h-full relative">
          {baseImages.length > 0 ? (
            <>
              {/* Base Simulator Images - LCP OPTIMIZED (SIMPLE) */}
              <div className="w-full h-full p-2 lg:p-6">
                <img 
                  src={baseImages[currentImageIndex]}
                  alt={`Base Simulator ${currentImageIndex + 1}`}
                  // KEY FIX: First image loads eagerly with high priority
                  loading={currentImageIndex === 0 ? "eager" : "lazy"}
                  fetchpriority={currentImageIndex === 0 ? "high" : "auto"}
                  decoding="async"
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.querySelector('.base-fallback').style.display = 'flex';
                  }}
                />
                <div className="base-fallback w-full h-full bg-zinc-400 rounded-lg items-center justify-center text-zinc-600 text-5xl lg:text-[20rem]" style={{ display: 'none' }}>
                  🏎️
                </div>
              </div>
              
              {/* Navigation Controls - Only show if more than 1 image */}
              {baseImages.length > 1 && (
                <>
                  {/* Left Arrow */}
                  <button
                    onClick={goToPrevious}
                    className="absolute left-4 lg:left-8 top-1/2 transform -translate-y-1/2 w-6 h-6 lg:w-8 lg:h-8 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    aria-label="Previous image"
                  >
                    <svg 
                      className="w-3 h-3 lg:w-4 lg:h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Right Arrow */}
                  <button
                    onClick={goToNext}
                    className="absolute right-4 lg:right-8 top-1/2 transform -translate-y-1/2 w-6 h-6 lg:w-8 lg:h-8 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    aria-label="Next image"
                  >
                    <svg 
                      className="w-3 h-3 lg:w-4 lg:h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Image Navigation Dots - Enhanced with click functionality */}
              {baseImages.length > 1 && (
                <div className="absolute bottom-9 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {baseImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 lg:w-3 lg:h-3 rounded-full transition-all duration-200 ${
                        index === currentImageIndex 
                          ? 'bg-white scale-125 shadow-lg' 
                          : 'bg-white/50 hover:bg-white/75 hover:scale-110'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Keyboard Navigation Hint - Show briefly on hover */}
              <div className="absolute bottom-16 lg:bottom-20 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-xs opacity-0 hover:opacity-100 transition-opacity duration-300 backdrop-blur-sm pointer-events-none">
                Use ← → keys to navigate
              </div>
            </>
          ) : (
            /* Fallback if no base images */
            <div className="flex items-center justify-center h-full">
              <div className="text-zinc-600 text-center">
                <div className="font-semibold mb-2 text-sm lg:text-lg">Select a Category</div>
                <div className="text-xs lg:text-sm opacity-75">Choose from {mainCategories.length} main categories</div>
              </div>
            </div>
          )}
        </div>
      ) : viewState === 'sub' && selectedSubCategory ? (
        <div className="w-full h-full p-2 lg:p-6 flex items-center justify-center">
          <div className="w-full h-full flex items-center justify-center text-zinc-600">
            {(() => {
              const iconElement = getIconForCategory(selectedSubCategory, categoryConfig);
              
              // If it's a React element (image), show as full-size image
              if (React.isValidElement(iconElement)) {
                return React.cloneElement(iconElement, {
                  className: "w-full h-full object-contain rounded-lg",
                  loading: "lazy",
                  decoding: "async",
                  onError: (e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.querySelector('.icon-fallback').style.display = 'flex';
                  }
                });
              }
              
              // Otherwise show emoji at full size
              return (
                <div className="text-[12rem] lg:text-[20rem] leading-none">
                  {iconElement}
                </div>
              );
            })()}
            <div className="icon-fallback w-full h-full items-center justify-center text-[12rem] lg:text-[20rem] leading-none" style={{ display: 'none' }}>
              ⚙️
            </div>
          </div>
        </div>
      ) : viewState === 'sub' && selectedMainCategory ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-full h-full flex items-center justify-center text-zinc-600">
            {(() => {
              const iconElement = getIconForCategory(selectedMainCategory, categoryConfig);
              
              // If it's a React element (image), show as full-size image
              if (React.isValidElement(iconElement)) {
                return React.cloneElement(iconElement, {
                  className: "w-full h-full object-contain",
                  loading: "lazy",
                  decoding: "async",
                  onError: (e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.querySelector('.icon-fallback').style.display = 'flex';
                  }
                });
              }
              
              // Otherwise show emoji at full size
              return (
                <div className="text-[12rem] lg:text-[20rem] leading-none">
                  {iconElement}
                </div>
              );
            })()}
            <div className="icon-fallback w-full h-full items-center justify-center text-[12rem] lg:text-[20rem] leading-none" style={{ display: 'none' }}>
              ⚙️
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-zinc-600 text-center">
            <div className="text-2xl lg:text-4xl mb-2">📦</div>
            <div className="text-sm lg:text-base">Select a product to preview</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductViewer;